import { FC, SyntheticEvent, useCallback, useEffect, useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { FormattedMessage, useIntl } from 'react-intl';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import Card from 'components/UI/Card';
import * as routes from 'router/Routes';
import PageWrapper from 'components/UI/PageWrapper';
import { Grid, ButtonGrid } from './styled';
import InputField from 'components/UI/InputField';
import Button from 'components/UI/Button';
import SelectField from 'components/UI/SelectField';
import {
  AuditConfig,
  BasicData,
  CFS,
  HusqiotBusAttribute,
  Template,
  XmlFilesContent,
} from 'models/husqiotbus';
import { FourColumnGrid } from 'styles';
import {
  templateSelector,
  configSelector,
  fetchTemplates,
  storeConfig,
  updateCheckedType,
  updateSelServiceFromType,
  basicDataSelector,
  auditConfigSelector,
} from 'store/husqiotbusState/husqiotbusState';
import { HusqiotbusConfig } from 'models/husqiotbus';
import Loader from 'components/UI/Loader';
import CheckboxCard from 'components/husqiotBus/CheckboxCard';
import DivertsCard from 'components/husqiotBus/DivertsCard';
import { showErrorToast } from 'utils/toast';
import CardHeader from 'components/UI/Card/CardHeader';
import DIRECTORY from 'services/husqiotBusService/fileService';
import { generateXmlConfigsForService } from 'services/husqiotBusService/generateConfigService';
import HusqiotBusOutput from 'components/husqiotBus/HusqiotBusOutput';

const HusqiotBusPage: FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const {
    selectedService,
    isLoading,
    isServiceLoading,
    isXmlLoading,
  }: HusqiotbusConfig = useAppSelector(configSelector);
  const templates: Template[] = useAppSelector(templateSelector);
  const basicData: BasicData = useAppSelector(basicDataSelector);
  const auditConfig: AuditConfig = useAppSelector(auditConfigSelector);

  // Watch
  const [
    serviceName,
    diamServiceName,
    contactEmail,
    xmlAddresses,
    xmlDevDiverts,
    xmlQaDiverts,
    xmlLiveDiverts,
    xmlHusqiotExtensions,
    xmlSecuritSettings,
  ] = watch([
    HusqiotBusAttribute.ServiceName,
    HusqiotBusAttribute.DiamServiceName,
    HusqiotBusAttribute.ContactEmail,
    HusqiotBusAttribute.XmlAddresses,
    HusqiotBusAttribute.XmlDevDiverts,
    HusqiotBusAttribute.XmlQaDiverts,
    HusqiotBusAttribute.XmlLiveDiverts,
    HusqiotBusAttribute.XmlHusqiotExtensions,
    HusqiotBusAttribute.XmlSecuritSettings,
  ]);

  useEffect(() => {
    if (DIRECTORY.directoryHandle) {
      dispatch(fetchTemplates());
    }
  }, [dispatch]);

  const updateService = useCallback(
    (name: HusqiotBusAttribute, value: string) => {
      dispatch(updateSelServiceFromType(name, value, basicData, serviceName));
    },
    [dispatch, basicData, serviceName]
  );

  const onSelect = useCallback(
    (name: HusqiotBusAttribute) => (value: string) => {
      updateService(name, value);
      setValue(name, value);
    },
    [setValue, updateService]
  );

  const onSave = useCallback(() => {
    if (!selectedService) {
      showErrorToast(intl.formatMessage({ id: 'husqiotbus.no_service' }));
      return;
    }
    let xmlConfig: XmlFilesContent | undefined = selectedService.xml;
    if (
      xmlAddresses ||
      xmlDevDiverts ||
      xmlQaDiverts ||
      xmlLiveDiverts ||
      xmlHusqiotExtensions ||
      xmlSecuritSettings
    ) {
      const {
        addresses,
        security,
        devDiverts,
        qaDiverts,
        liveDiverts,
        extension,
      } = generateXmlConfigsForService(
        serviceName || selectedService.name,
        diamServiceName || selectedService.diamServiceName,
        templates,
        selectedService,
        basicData,
        auditConfig
      );
      xmlConfig = {
        addresses: xmlAddresses || selectedService.xml?.addresses || addresses,
        security:
          xmlSecuritSettings || selectedService.xml?.security || security,
        extension:
          xmlHusqiotExtensions || selectedService.xml?.extension || extension,
        devDiverts:
          xmlDevDiverts || selectedService.xml?.devDiverts || devDiverts,
        liveDiverts:
          xmlLiveDiverts || selectedService.xml?.liveDiverts || liveDiverts,
        qaDiverts: xmlQaDiverts || selectedService.xml?.qaDiverts || qaDiverts,
      };
    }
    dispatch(
      storeConfig(
        serviceName,
        diamServiceName,
        contactEmail,
        selectedService,
        xmlConfig
      )
    );
    navigate(`${routes.HUSQIOTBUS}`);
  }, [
    dispatch,
    selectedService,
    diamServiceName,
    contactEmail,
    serviceName,
    navigate,
    intl,
    xmlAddresses,
    xmlDevDiverts,
    xmlQaDiverts,
    xmlLiveDiverts,
    xmlHusqiotExtensions,
    xmlSecuritSettings,
    basicData,
    templates,
    auditConfig,
  ]);

  const onCheckboxChange = useCallback(
    (name: HusqiotBusAttribute) => (value: SyntheticEvent) => {
      dispatch(updateCheckedType(name, value));
    },
    [dispatch]
  );

  // Submit
  const onSubmit: SubmitHandler<any> = useCallback(() => {
    if (
      selectedService?.type === CFS &&
      selectedService.authorizers.filter((auth) => auth.checked).length < 1
    ) {
      showErrorToast(intl.formatMessage({ id: 'husqiotbus.no_authorizer' }));
    } else {
      onSave();
    }
  }, [onSave, selectedService, intl]);

  const onChangeXml = useCallback(
    (name: string, value: string) => {
      setValue(name, value);
    },
    [setValue]
  );

  const baseInfo = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    return (
      <div>
        <Card>
          <CardHeader navUrl={routes.HUSQIOTBUS} title="husqiotbus.btn_back" />
          <FourColumnGrid>
            <InputField
              description="husqiotbus.new_service"
              defaultValue={selectedService?.name}
              error={errors.serviceName}
              register={register(HusqiotBusAttribute.ServiceName, {
                required: {
                  value: true,
                  message: 'input.required',
                },
              })}
            />
            <InputField
              description="husqiotbus.diam_client"
              defaultValue={selectedService?.diamServiceName}
              error={errors.diamServiceName}
              register={register(HusqiotBusAttribute.DiamServiceName, {
                required: {
                  value: true,
                  message: 'input.required',
                },
              })}
            />
            <SelectField
              name={HusqiotBusAttribute.ServiceType}
              defaultValue={selectedService?.type}
              onSelect={onSelect(HusqiotBusAttribute.ServiceType)}
              label={intl.formatMessage({ id: 'husqiotbus.service_type' })}
              options={basicData.serviceTypes}
            />
            <InputField
              description="husqiotbus.contact_email"
              defaultValue={selectedService?.contactEmail}
              error={errors.contactEmail}
              register={register(HusqiotBusAttribute.ContactEmail, {
                required: {
                  value: true,
                  message: 'input.required',
                },
                validate: (value) =>
                  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ||
                  'input.invalid_email',
              })}
            />
          </FourColumnGrid>
        </Card>
      </div>
    );
  }, [
    selectedService,
    isLoading,
    onSelect,
    intl,
    register,
    errors,
    basicData,
    // onEnvCheckboxChange,
  ]);

  const specificInfo = useMemo(() => {
    if (isServiceLoading) {
      return <Loader center />;
    }
    if (!selectedService || selectedService.xml) {
      return null;
    }
    return (
      <div>
        <DivertsCard headingId="husqiotbus.diverts" />
        <FourColumnGrid>
          <CheckboxCard
            checkboxTypes={selectedService.authorizers}
            headingId="husqiotbus.authorizers"
            onChange={onCheckboxChange(HusqiotBusAttribute.Authorizers)}
          />
          <div>
            <CheckboxCard
              checkboxTypes={selectedService.addresses || []}
              headingId="husqiotbus.addresses"
              onChange={onCheckboxChange(HusqiotBusAttribute.Addresses)}
            />
            <CheckboxCard
              checkboxTypes={selectedService.annotators}
              headingId="husqiotbus.annotators"
              onChange={onCheckboxChange(HusqiotBusAttribute.Annotators)}
            />
          </div>
          <CheckboxCard
            checkboxTypes={selectedService.acceptors}
            headingId="husqiotbus.acceptors"
            onChange={onCheckboxChange(HusqiotBusAttribute.Acceptors)}
          />
          <CheckboxCard
            checkboxTypes={selectedService.validators}
            headingId="husqiotbus.validators"
            onChange={onCheckboxChange(HusqiotBusAttribute.Validators)}
          />
        </FourColumnGrid>
      </div>
    );
  }, [selectedService, isServiceLoading, onCheckboxChange]);

  const commitCard = useMemo(() => {
    if (isServiceLoading) {
      return <Loader center />;
    }
    if (!selectedService) {
      return null;
    }
    return (
      <Card>
        <Grid>
          <ButtonGrid>
            <Button submit>
              <FormattedMessage id="husqiotbus.btn_save" />
            </Button>
          </ButtonGrid>
        </Grid>
      </Card>
    );
  }, [selectedService, isServiceLoading]);

  const xmlData = useMemo(() => {
    if (isLoading || isServiceLoading || isXmlLoading) {
      return <Loader center />;
    }
    if (
      !selectedService ||
      templates.length <= 0 ||
      !templates[0] ||
      !basicData
    ) {
      return null;
    }
    if (selectedService.xml) {
      return (
        <HusqiotBusOutput
          addresses={selectedService.xml.addresses}
          security={selectedService.xml.security}
          devDiverts={selectedService.xml.devDiverts}
          qaDiverts={selectedService.xml.qaDiverts}
          liveDiverts={selectedService.xml.liveDiverts}
          extension={selectedService.xml.extension}
          onChange={onChangeXml}
        />
      );
    }
    const {
      addresses,
      security,
      devDiverts,
      qaDiverts,
      liveDiverts,
      extension,
    } = generateXmlConfigsForService(
      serviceName || selectedService.name,
      diamServiceName || selectedService.diamServiceName,
      templates,
      selectedService,
      basicData,
      auditConfig
    );
    return (
      <HusqiotBusOutput
        addresses={addresses}
        security={security}
        devDiverts={devDiverts}
        qaDiverts={qaDiverts}
        liveDiverts={liveDiverts}
        extension={extension}
        onChange={onChangeXml}
      />
    );
  }, [
    serviceName,
    templates,
    diamServiceName,
    selectedService,
    isLoading,
    isXmlLoading,
    isServiceLoading,
    basicData,
    auditConfig,
    onChangeXml,
  ]);

  return (
    <PageWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        {baseInfo}
        {specificInfo}
        {xmlData}
        {commitCard}
      </form>
    </PageWrapper>
  );
};

export default HusqiotBusPage;
